import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-google-charts";
import { useTranslation } from "react-i18next";
import { Lightbulb, BarChart2, TrendingUp, Calendar, ChevronDown } from "lucide-react";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {alignTimeSeries, COMPARISON_COLOR, comparisonLegend, dashedComparisonSeries} from "../comparison";
import {formatCurrency} from "../dashboard/formatters";

const ChartSection = ({ _dateRange, _startTime, _endTime, isDarkMode = false, refreshKey = 0, comparisonRange = null }) => {
    const {t} = useTranslation("activite");
    const [filteredData, setFilteredData] = useState([]);
    // Rows for the comparison period (null when comparison is off).
    const [previousData, setPreviousData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState("AreaChart");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const axisTextColor = isDarkMode ? '#9CA3AF' : '#6B7280';
    const axisTitleColor = isDarkMode ? '#D1D5DB' : '#374151';
    const options = {
        hAxis: {
            textStyle: { fontSize: 12, color: axisTextColor },
            titleTextStyle: { fontSize: 14, bold: true, color: axisTitleColor },
            slantedText: true,
            slantedTextAngle: 45
        },
        vAxis: {
            minValue: 0,
            textStyle: { fontSize: 12, color: axisTextColor },
            titleTextStyle: { fontSize: 14, bold: true, color: axisTitleColor }
        },
        legend: "none",
        chartArea: { width: "80%", height: "70%" },
        colors: ["#4DB6AC"],
        backgroundColor: { fill: "transparent" },
        tooltip: { textStyle: { fontSize: 12 }, showColorCode: true },
        ...(previousData ? {
            colors: ["#4DB6AC", COMPARISON_COLOR],
            legend: comparisonLegend(isDarkMode),
            series: dashedComparisonSeries,
        } : {})
    };

    useEffect(() => {
        const fetchSalesData = async () => {
            setLoading(true);
            try {
                const startTimeFormatted = _startTime || "00:00";
                const endTimeFormatted   = _endTime || "23:59";

                const accountId = await secureStorage.getItem("selectedAccount");

                const fetchRange = (range) => api.get(
                    `/activite/get_sales_by_date?startDate=${toLocalDateStr(range.startDate)}&endDate=${toLocalDateStr(range.endDate)}&startTime=${startTimeFormatted}&endTime=${endTimeFormatted}`,
                    {
                        headers: {
                            "X-account-id": accountId.id,
                            "Accept": "application/json",
                            "Content-Type": "application/json"
                        }
                    }
                );

                const [response, previousResponse] = await Promise.all([
                    fetchRange(_dateRange[0]),
                    comparisonRange?.[0] ? fetchRange(comparisonRange[0]) : Promise.resolve(null),
                ]);

                // Keep raw rows only — the header (translated) is prepended at
                // render time so it stays in sync when the language changes.
                setFilteredData(response.data);
                setPreviousData(previousResponse ? (previousResponse.data || []) : null);
            } catch (error) {
                console.error("Error fetching sales data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSalesData();
    }, [_dateRange, _startTime, _endTime, refreshKey, comparisonRange]);

    // Chart rows. With a comparison, each day is paired with the same day of
    // the comparison period (dashed grey series, its own date in the tooltip).
    const chartData = useMemo(() => {
        const toSeries = (rows) => (rows || []).map(([label, value]) => ({label, value: Number(value) || 0}));
        const revenueColumn = t("chartSection.revenueColumn");
        if (!previousData) {
            return [["Date", revenueColumn], ...filteredData];
        }
        const previousName = t("comparison.previousSeries");
        const aligned = alignTimeSeries(toSeries(filteredData), toSeries(previousData), _dateRange, comparisonRange);
        return [
            ["Date", revenueColumn, previousName, {type: "string", role: "tooltip"}],
            ...aligned.map(r => [
                r.label,
                r.value,
                r.previous,
                `${r.previousLabel}\n${previousName}: ${formatCurrency(r.previous)}`,
            ]),
        ];
    }, [filteredData, previousData, _dateRange, comparisonRange, t]);

    const hasData = filteredData.length > 0 || (previousData?.length ?? 0) > 0;

    // Simple date formatter that doesn't rely on complex locale methods
    const formatDateRange = () => {
        try {
            const startDate = new Date(_dateRange[0].startDate);
            const endDate = new Date(_dateRange[0].endDate);

            const formatDate = (date) => {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            };

            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        } catch (error) {
            return t("chartSection.selectedPeriod");
        }
    };

    return (
        <div className={`shadow-sm border-r-2 overflow-hidden h-full w-full ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h2 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            <BarChart2 className="w-5 h-5 mr-2 text-teal-500" strokeWidth={2} />
                            {t("chartSection.title")}
                        </h2>
                    </div>

                    <div className="relative">
                        <button
                            className={`flex items-center justify-between px-3 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-200' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'}`}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <span className="text-sm mr-2">{t("chartSection.type")}</span>
                            <ChevronDown className="w-4 h-4" />
                        </button>

                        {isDropdownOpen && (
                            <div className={`absolute right-0 mt-1 w-44 border rounded-lg shadow-lg z-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                <div className="py-1">
                                    {[
                                        { id: 'AreaChart', name: t("chartSection.chartTypes.area"), icon: <TrendingUp className="w-4 h-4" /> },
                                        { id: 'LineChart', name: t("chartSection.chartTypes.line"), icon: <TrendingUp className="w-4 h-4" /> },
                                        { id: 'ColumnChart', name: t("chartSection.chartTypes.column"), icon: <BarChart2 className="w-4 h-4" /> }
                                    ].map(type => (
                                        <button
                                            key={type.id}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${chartType === type.id ? 'bg-teal-50 text-teal-700' : isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                            onClick={() => {
                                                setChartType(type.id);
                                                setIsDropdownOpen(false);
                                            }}
                                        >
                                            <span className="mr-2">{type.icon}</span>
                                            {type.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center" style={{ minHeight: "280px" }}>
                        <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin"></div>
                        <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t("chartSection.loading")}</p>
                    </div>
                ) : hasData ? (
                    <Chart
                        chartType={chartType}
                        width="100%"
                        height="100%"
                        data={chartData}
                        options={options}
                        className="w-full h-full"
                        style={{ minHeight: "280px" }}
                    />
                ) : (
                    <div
                        className={`flex flex-col items-center justify-center w-full text-center rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        style={{ minHeight: "280px" }}
                    >
                        <div className="mb-4 bg-yellow-100 p-4 rounded-full">
                            <Lightbulb size={40} className="text-yellow-500" strokeWidth={1.5} />
                        </div>
                        <h3 className={`text-base font-medium mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {t("chartSection.noDataTitle")}
                        </h3>
                        <p className={`text-sm max-w-md px-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {t("chartSection.noDataBody")}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChartSection;
